import { AngularFirestore } from '@angular/fire/firestore';
import { AuthUserService } from '../auth/auth-user.service';
import { DocumentReference } from '@firebase/firestore-types';
import { Injectable } from '@angular/core';
import { NfmbDataService } from '../nfmb-data.service';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { Profile, Profiles } from './profile.model';
import { switchMap, first, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProfileService extends NfmbDataService {

  private groupFilter$: BehaviorSubject<string|null>;

  constructor(
    afs: AngularFirestore,
    authUserService: AuthUserService
  ) {
    super(afs, authUserService, 'profiles');
    this.groupFilter$ = new BehaviorSubject(null);

    this.permissions = {
      edit: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN,
        Profiles.Role.EDITOR
      ],
      delete: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN
      ],
      read: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN,
        Profiles.Role.EDITOR,
        Profiles.Role.READER
      ],
      viewGroups: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN
      ],
      viewAll: [
        Profiles.Role.ROOT,
        Profiles.Role.ADMIN
      ]
    };
  }

  get data$(): Observable<any> {
    return combineLatest([
      this.groupFilter$,
      this.authUserService.currentProfile$
    ]).pipe(
      switchMap( ([group, profile]) => {
        if (profile) {
          return this.afs.collection<Profile>(this.collectionPath,
            ref => {
              console.log('Profile Stream emit new value for following group and profile: ', { group, profile });

              /**
               * If current profile has permissions to view all, filter by type is disabled
               */
              const roles = (profile && profile.roles) ? profile.roles : [];
              const canViewAll = this.haveRolesPermissionsTo(roles, 'viewAll');

              let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
              query = query.where('organizationId', '==', profile.organizationId);
              if (group) { query = query.where(Profiles.IndexField.GROUPS, 'array-contains', group); }
              query = query.orderBy(Profiles.IndexField.CREATED_AT, 'desc');
              if (!canViewAll) {
                query = query.where(Profiles.IndexField.TYPE, '==', Profiles.Type.USER);
              }
              return query;
            }
          ).valueChanges({ idField: 'id' });
        } else {
          return [];
        }
      }),
      shareReplay(1)
    );
  }

  async push(item: any): Promise<DocumentReference> {
    const currentProfile = await this.authUserService.currentProfile$.pipe(first()).toPromise();
    if (!(currentProfile && currentProfile.organizationId)) {
      return Promise.reject('user not authorized');
    }
    const timestamp = this.timestamp;
    return this.afs.collection<Profile>(this.collectionPath).add({
      ...item,
      organizationId: currentProfile.organizationId,
      createdAt: timestamp,
      modifiedAt: timestamp,
    });
  }

  public filterByGroup(group: string): void {
    return this.groupFilter$.next(group);
  }

  public resetPublicFilters(): void {
    return this.groupFilter$.next(null);
  }
}
